import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';

const DropdownNav = ({ navItems }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubitem, setActiveSubitem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (itemId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(itemId);
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setActiveSubitem(null);
      setHoveredItem(null);
    }, 150); // Small delay for better UX
  };

  const handleSubitemHover = (subitemLabel) => {
    setActiveSubitem(subitemLabel);
  };

  const handleSubitemLeave = () => {
    setTimeout(() => setActiveSubitem(null), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <nav className="dark-nav hidden lg:flex items-center space-x-8">
      {navItems.map((item) => (
        <div
          key={item.id}
          className="relative group"
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={handleMouseLeave}
        >
          {/* Main nav item - Clean design without arrows */}
          {item.onClick ? (
            <button
              onClick={item.onClick}
              className="flex items-center gap-2 dark-nav-link hover:text-emerald-300 transition-colors py-2 whitespace-nowrap"
              role="button"
            >
              {item.label}
            </button>
          ) : (
            <a
              href={item.href || "#"}
              className="flex items-center gap-2 dark-nav-link hover:text-emerald-300 transition-colors py-2 whitespace-nowrap"
              role={item.dropdown ? "button" : "link"}
              aria-haspopup={item.dropdown ? "true" : "false"}
              aria-expanded={activeDropdown === item.id ? "true" : "false"}
            >
              {item.label}
              {item.dropdown && (
                <ChevronDown 
                  className={`w-3 h-3 transition-transform duration-200 opacity-60 ${
                    activeDropdown === item.id ? 'rotate-180' : ''
                  }`} 
                />
              )}
            </a>
          )}

          {/* Dropdown menu - SIMPLE VISIBILITY FIX */}
          {item.dropdown && activeDropdown === item.id && (
            <div 
              className="dropdown-menu"
              style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                border: '1px solid rgba(0, 255, 209, 0.2)',
                borderRadius: '8px',
                minWidth: '280px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(8px)',
                marginTop: '8px',
                display: 'block',
                opacity: 1,
                visibility: 'visible'
              }}
              onMouseEnter={() => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
              }}
              onMouseLeave={handleMouseLeave}
            >
              <div className="py-2">
                {item.dropdown.map((dropdownItem, index) => (
                  <div key={`${item.id}-${index}`}>
                    {dropdownItem.subitems ? (
                      // Item with subitems - Enhanced for mobile/tablet compatibility
                      <div
                        className="relative"
                        onMouseEnter={() => handleSubitemHover(dropdownItem.label)}
                        onMouseLeave={handleSubitemLeave}
                      >
                        <div className="flex items-center justify-between px-4 py-3 text-gray-300 hover:text-emerald-300 hover:bg-white/5 transition-all duration-200 cursor-pointer group/item">
                          <span className="font-medium">{dropdownItem.label}</span>
                          <ArrowRight className="w-4 h-4 transform group-hover/item:translate-x-1 transition-transform" />
                        </div>
                        
                        {/* Subitems - Improved positioning */}
                        {activeSubitem === dropdownItem.label && (
                          <div className="absolute left-full top-0 min-w-[300px] bg-black/95 backdrop-filter backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl ml-2 z-[1001]">
                            <div className="py-2">
                              {dropdownItem.subitems.map((subitem, subIndex) => (
                                <a
                                  key={`${dropdownItem.label}-${subIndex}`}
                                  href={subitem.href}
                                  className="flex items-center justify-between px-4 py-2 text-sm text-gray-400 hover:text-emerald-300 hover:bg-white/5 transition-all duration-200 group/subitem"
                                >
                                  <span>{subitem.label}</span>
                                  <ArrowRight className="w-3 h-3 opacity-0 group-hover/subitem:opacity-100 transform group-hover/subitem:translate-x-1 transition-all" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Regular dropdown item
                      dropdownItem.onClick ? (
                        <button
                          onClick={dropdownItem.onClick}
                          className="w-full flex items-center justify-between px-4 py-2 text-gray-300 hover:text-emerald-300 hover:bg-white/5 transition-all duration-200 group/link text-left"
                        >
                          <span>{dropdownItem.label}</span>
                          <ArrowRight className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transform group-hover/link:translate-x-1 transition-all" />
                        </button>
                      ) : (
                        <a
                          href={dropdownItem.href}
                          target={dropdownItem.external ? "_blank" : "_self"}
                          rel={dropdownItem.external ? "noopener noreferrer" : ""}
                          className="flex items-center justify-between px-4 py-2 text-gray-300 hover:text-emerald-300 hover:bg-white/5 transition-all duration-200 group/link"
                        >
                          <span>{dropdownItem.label}</span>
                          <ArrowRight className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transform group-hover/link:translate-x-1 transition-all" />
                        </a>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </nav>
  );
};

export default DropdownNav;